import { useState, useRef, useEffect } from "react";
import { type ThemeObject, SP, RADIUS, SHADOW, TYPE } from "../shared/theme.js";

interface TwoFactorVerifyProps {
  T: ThemeObject;
  onVerify: (code: string) => Promise<void> | void;
  onUseBackup?: () => void;
}

export default function TwoFactorVerify({ T, onVerify, onUseBackup }: TwoFactorVerifyProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus no input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-submit quando atingir 6 dígitos
  useEffect(() => {
    if (code.length === 6) {
      handleSubmit();
    }
  }, [code]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      if (code.length !== 6) {
        throw new Error("Código deve ter 6 dígitos");
      }

      if (!/^\d+$/.test(code)) {
        throw new Error("Código deve conter apenas números");
      }

      // Chamar callback
      await onVerify?.(code);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setCode("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navy2} 100%)`,
        padding: SP[4],
        fontFamily: TYPE.fontSans,
      }}
    >
      <div
        style={{
          background: T.card,
          borderRadius: RADIUS.lg,
          border: `1px solid ${T.border}`,
          boxShadow: SHADOW.lg,
          padding: SP[8],
          maxWidth: 420,
          width: "100%",
          textAlign: "center",
        }}
      >
        {/* Ícone */}
        <div style={{ fontSize: 48, marginBottom: SP[4] }}>🔐</div>

        {/* Cabeçalho */}
        <h1
          style={{
            fontSize: TYPE["2xl"],
            fontWeight: TYPE.bold,
            color: T.text,
            margin: `0 0 ${SP[2]}`,
          }}
        >
          Verificação 2FA
        </h1>
        <p
          style={{
            fontSize: TYPE.base,
            color: T.sub,
            margin: `0 0 ${SP[6]}`,
            lineHeight: TYPE.relaxed,
          }}
        >
          Digite os 6 dígitos do seu aplicativo de autenticação.
        </p>

        {/* Erro */}
        {error && (
          <div
            style={{
              background: "#DC262615",
              border: "1px solid #DC2626",
              borderRadius: RADIUS.md,
              padding: SP[3],
              marginBottom: SP[4],
              fontSize: TYPE.sm,
              color: "#DC2626",
            }}
          >
            {error}
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            maxLength={6}
            pattern="\d{6}"
            value={code}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\D/g, "").slice(0, 6);
              setCode(cleaned);
              setError(null);
            }}
            placeholder="000000"
            disabled={loading}
            style={{
              width: "100%",
              padding: SP[4],
              fontSize: "32px",
              textAlign: "center",
              letterSpacing: "12px",
              border: `2px solid ${error ? "#DC2626" : T.border}`,
              borderRadius: RADIUS.md,
              color: T.text,
              background: T.surface,
              marginBottom: SP[4],
              fontFamily: "monospace",
              fontWeight: TYPE.bold,
              transition: "border-color 0.25s",
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "text",
            }}
          />

          <button
            type="submit"
            disabled={loading || code.length < 6}
            style={{
              width: "100%",
              padding: `${SP[3]} ${SP[4]}`,
              background: code.length === 6 && !loading ? T.accent : T.muted,
              color: T.accentText,
              border: "none",
              borderRadius: RADIUS.md,
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              cursor: loading || code.length < 6 ? "not-allowed" : "pointer",
              transition: "all 0.25s",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Verificando..." : "Verificar"}
          </button>
        </form>

        {/* Link para código de backup */}
        <button
          onClick={onUseBackup}
          disabled={loading}
          style={{
            marginTop: SP[4],
            background: "none",
            border: "none",
            color: T.accent,
            fontSize: TYPE.sm,
            cursor: loading ? "not-allowed" : "pointer",
            textDecoration: "underline",
            fontWeight: TYPE.semibold,
            opacity: loading ? 0.6 : 1,
          }}
        >
          Usar código de backup
        </button>

        {/* Info */}
        <p
          style={{
            fontSize: TYPE.xs,
            color: T.muted,
            margin: `${SP[6]} 0 0`,
            lineHeight: TYPE.relaxed,
          }}
        >
          Não consegue acessar seu aplicativo?
          <br />
          Use um dos códigos de backup fornecidos durante a configuração.
        </p>
      </div>
    </div>
  );
}
